import curatedRaw from "@/data/properties-curated.json";

interface RawCurated {
  region: string;
  regionJa: string;
  type: string;
  address: string;
  notes: string;
  priceJPY: number | null;
  priceUSD: number | null;
  rentJPY: number | null;
  rentUSD: number | null;
  yieldPct: number | null;
  transit: string;
  busStop?: string;
  viewing: string;
  rebuildable: string;
  parking: string;
  yearBuiltRaw: string;
  yearBuilt: number | null;
  floorPlan?: string | null;
  buildingArea?: string | null;
  landArea?: string | null;
  structure?: string;
  id: string;
  slug?: string;
  typeLabel: string;
  priceBand: string;
  priceJPYDisplay: string;
  priceJPYFormatted: string;
  priceUSDFormatted: string | null;
  rentJPYFormatted: string | null;
  rentUSDFormatted: string | null;
  cityEN: string;
  transitEN: string | null;
  notesEN: string;
  parkingEN: string | null;
  rebuildEN: string | null;
  viewingEN: string | null;
  headline: string;
  highlight: string;
  descriptionEN: string;
  gallery: { src: string; alt: string; type: "cg" | "current" }[];
  image: string;
  regionDisplay: string;
  regionFlavor: string;
  status: string;
  featured?: boolean;
  rakumachiUrl?: string;
}

const curated = curatedRaw as RawCurated[];

// ─────────────────────────────────────────────────────────────
// Type mapping: collapse raw regions/types into existing UI types
// ─────────────────────────────────────────────────────────────
function uiType(p: RawCurated): string {
  if (p.type === "Apartment") return "Apartment blocks";
  if (p.type === "Condo") return "Condos";
  if (p.type === "House" && p.yearBuilt && p.yearBuilt < 1965) return "Kominka";
  if (p.type === "House" && /Historic kominka/.test(p.headline)) return "Kominka";
  return "Houses";
}

function uiTypeLabel(t: string): string {
  switch (t) {
    case "Kominka": return "Kominka";
    case "Houses": return "Detached House";
    case "Apartment blocks": return "Apartment Building";
    case "Condos": return "Condo";
    default: return t;
  }
}

// Estimate bedrooms from notes & type
function estimateBedrooms(p: RawCurated): number {
  const m = (p.notes || "").match(/(\d+)\s*[LSDK]+/i);
  if (m) return parseInt(m[1], 10);
  if (p.type === "Condo") return 1;
  if (p.type === "Apartment") return 0;
  return 3;
}

function areaFor(p: RawCurated): string {
  if (p.buildingArea) return p.buildingArea;
  if (p.landArea) return p.landArea;
  return "—";
}

function landFor(p: RawCurated): string {
  if (p.landArea) return p.landArea;
  if (p.type === "Condo") return "N/A";
  return "—";
}

// ─────────────────────────────────────────────────────────────
// Public shape (matches what existing UI components consume)
// ─────────────────────────────────────────────────────────────
export const sampleProperties = curated.map((p) => {
  const type = uiType(p);
  return {
    id: p.id,
    region: `${p.regionDisplay} · ${p.cityEN}`,
    regionDisplay: p.regionDisplay,
    cityEN: p.cityEN,
    priceBand: p.priceBand,
    priceJPY: p.priceJPYFormatted,
    priceUSD: p.priceUSDFormatted || "Inquire",
    priceJPYNumber: p.priceJPY,
    priceUSDNumber: p.priceUSD,
    rentJPY: p.rentJPYFormatted,
    rentUSD: p.rentUSDFormatted,
    yieldPct: p.yieldPct,
    type,
    typeLabel: uiTypeLabel(type),
    bedrooms: estimateBedrooms(p),
    area: areaFor(p),
    landArea: landFor(p),
    yearBuilt: p.yearBuilt || 0,
    yearBuiltDisplay: p.yearBuilt ? String(p.yearBuilt) : "TBD",
    description: p.descriptionEN,
    image: p.image,
    headline: p.headline,
    highlight: p.highlight,
    transitEN: p.transitEN,
    parkingEN: p.parkingEN,
    rebuildEN: p.rebuildEN,
    viewingEN: p.viewingEN,
    notesEN: p.notesEN,
    status: p.status,
    featured: !!p.featured,
    rakumachiUrl: p.rakumachiUrl,
  };
});

export const featuredProperties = sampleProperties.filter((p) => p.featured);
export const regularProperties = sampleProperties.filter((p) => !p.featured);

export const propertyTypes = [
  { label: "All", value: "all" },
  { label: "Kominka", value: "Kominka" },
  { label: "Detached Houses", value: "Houses" },
  { label: "Apartment Blocks", value: "Apartment blocks" },
  { label: "Condos", value: "Condos" },
];

export const regions = [
  { name: "Kanagawa", tagline: "Yokosuka, Yokohama, Miura, Kamakura. Coastal & near Tokyo.", priceRange: "¥500K – ¥7M" },
  { name: "Saitama", tagline: "Kumagaya, Kawagoe, Chichibu, Sayama. Suburban & family belt.", priceRange: "¥1.4M – ¥6M" },
  { name: "Chiba", tagline: "Surf coast & inland. Tokyo access in 60–120 min.", priceRange: "¥200K – ¥4.3M" },
  { name: "Tokyo", tagline: "Tama, Ome, Hachioji. Inside Greater Tokyo, often under ¥10M.", priceRange: "¥4.5M – ¥30M" },
  { name: "Kanto-Tokai", tagline: "Shizuoka, Yamanashi, highland. Mountain belt.", priceRange: "¥500K – ¥6M" },
  { name: "Apartment", tagline: "Whole apartment blocks. Existing tenants, cash-flowing.", priceRange: "¥10M – ¥140M" },
  { name: "Condo", tagline: "Mid-rise concrete units. Turnkey, high liquidity.", priceRange: "¥2M – ¥26M" },
];

// ─────────────────────────────────────────────────────────────
// Property detail
// ─────────────────────────────────────────────────────────────
export interface PropertyDetail {
  id: string;
  gallery: { src: string; alt: string; type: "cg" | "current" | "factsheet" }[];
  opportunity: string;
  specs: Record<string, string | null>;
  renovationPlan: string[];
  indicativeReturn: { label: string; value: string; note?: string }[];
  areaNote: string;
  defaultAllIn: number;
}

// Build a default renovation plan based on type/age
function renovationPlanFor(p: RawCurated): string[] {
  const isKominka = (p.yearBuilt && p.yearBuilt < 1965) || /Historic kominka/.test(p.headline);
  const isCondo = p.type === "Condo";
  const isApartment = p.type === "Apartment";
  if (isCondo) {
    return [
      "Floor refinish, wall repaint, fixture replacement (¥600K–1.0M)",
      "Kitchen and bathroom modernization (¥800K–1.4M)",
      "Lighting and AC upgrade (¥250K–400K)",
      "Furniture and styling for STR or long-term let (¥400K–800K)",
    ];
  }
  if (isApartment) {
    return [
      "Common areas, exterior paint, entryway (¥1.5–3.0M)",
      "Unit-by-unit turnover refurbishment (¥500K–900K per unit)",
      "Roof, gutters, sealants inspection and repair (¥800K–1.5M)",
      "Branding, photography, leasing setup (¥300K–600K)",
    ];
  }
  if (isKominka) {
    return [
      "Structural assessment and full interior gut (¥1.2–1.8M)",
      "Electrical, plumbing, sewage modernization (¥800K–1.2M)",
      "Insulation, shoji restoration, tatami install (¥600K–900K)",
      "Kitchen and bathroom fit-out with modern fixtures (¥900K–1.3M)",
      "Exterior timber treatment, roof inspection, garden clearing (¥400K–700K)",
    ];
  }
  return [
    "Interior gut and structural assessment (¥800K–1.4M)",
    "Electrical, plumbing, water heater renewal (¥500K–900K)",
    "Insulation, flooring, interior finish (¥500K–800K)",
    "Kitchen and bathroom fit-out (¥700K–1.0M)",
    "Exterior maintenance and minor roof repair (¥300K–600K)",
  ];
}

function indicativeReturnFor(p: RawCurated): { label: string; value: string; note?: string }[] {
  const out: { label: string; value: string; note?: string }[] = [];
  out.push({ label: "Purchase price", value: p.priceJPYFormatted });
  const baseReno = p.type === "Apartment" ? "¥4–8M"
                 : p.type === "Condo" ? "¥2–4M"
                 : (p.yearBuilt && p.yearBuilt < 1965) ? "¥4–7M"
                 : "¥3–5M";
  out.push({ label: "Estimated renovation", value: baseReno, note: "Range depends on scope" });

  if (p.rentJPY) {
    out.push({ label: "Current/projected rent", value: `${p.rentJPYFormatted}`, note: p.rentUSDFormatted ? `≈ ${p.rentUSDFormatted}` : undefined });
  }
  if (p.yieldPct) {
    out.push({ label: "Yield on purchase price", value: `${p.yieldPct.toFixed(1)}%`, note: "Illustrative · pre-renovation" });
  }
  return out;
}

function specsFor(p: RawCurated): Record<string, string | null> {
  return {
    Price: p.priceJPYFormatted + (p.priceUSDFormatted ? ` · ${p.priceUSDFormatted}` : ""),
    Type: uiTypeLabel(uiType(p)),
    Location: `${p.regionDisplay} · ${p.cityEN}`,
    "Nearest station / access": p.transitEN || null,
    "Land area": p.landArea || null,
    "Building area": p.buildingArea || null,
    "Year built": p.yearBuilt ? String(p.yearBuilt) : "Pre-WWII or TBD",
    Structure: p.structure || null,
    Parking: p.parkingEN || null,
    Rebuildability: p.rebuildEN || null,
    Viewing: p.viewingEN || null,
    "Current rent (existing)": p.rentJPYFormatted || null,
    "Yield (raw)": p.yieldPct ? `${p.yieldPct.toFixed(1)}%` : null,
    "Transaction type": "Brokered sale · cash",
  };
}

function opportunityFor(p: RawCurated): string {
  const isKominka = (p.yearBuilt && p.yearBuilt < 1965) || /Historic kominka/.test(p.headline);
  const city = p.cityEN.split(" (")[0];
  if (p.type === "Apartment") {
    return `A whole apartment block in ${city}. Existing tenants generate cash flow from day one. Light cosmetic work between turnovers, with phased common-area refresh over 18–24 months. Suited to yield-focused investors seeking a stand-alone Japanese asset with operator support.`;
  }
  if (p.type === "Condo") {
    return `A mid-rise condo unit in ${city}. Reinforced-concrete construction with shared maintenance reserves. Renovation scope is finishes, fixtures and styling — no structural work needed. Best for buyers who want a turnkey rental or a clean Tokyo-area pied-à-terre.`;
  }
  if (isKominka) {
    return `A traditional kominka in ${city}. The timber frame and tiled roof are largely intact, but the interior requires a full gut renovation. Post-renovation, this property is positioned for short-term rental demand from Tokyo weekenders and international guests drawn to authentic Japanese living. ${p.notesEN}`;
  }
  return `A detached Japanese house in ${city}, ${p.yearBuilt ? `built ${p.yearBuilt}` : "year TBD"}. The structure is sound enough for a medium-scope renovation: systems modernization, kitchen and bathroom fit-out, and finishes. Suitable as a personal second home or as a long-term rental. ${p.notesEN}`.trim();
}

function areaNoteFor(p: RawCurated): string {
  const city = p.cityEN.split(" (")[0];
  return `${city} sits in ${p.regionDisplay}. ${p.regionFlavor} ${p.transitEN ? `Access: ${p.transitEN}.` : ""} Detailed neighborhood notes, photos of the actual property in its current condition, and our independent inspection report are shared after inquiry.`;
}

function defaultAllInFor(p: RawCurated): number {
  if (!p.priceJPY) return 0;
  const renoEst = p.type === "Apartment" ? 6_000_000
                : p.type === "Condo" ? 2_500_000
                : 4_500_000;
  return p.priceJPY + renoEst;
}

export const propertyDetails: Record<string, PropertyDetail> = Object.fromEntries(
  curated.map((p) => [
    p.id,
    {
      id: p.id,
      gallery: p.gallery,
      opportunity: opportunityFor(p),
      specs: specsFor(p),
      renovationPlan: renovationPlanFor(p),
      indicativeReturn: indicativeReturnFor(p),
      areaNote: areaNoteFor(p),
      defaultAllIn: defaultAllInFor(p),
    },
  ]),
);

export function getPropertyDetail(id: string): PropertyDetail | undefined {
  return propertyDetails[id];
}

export function parsePrice(p: string): number {
  if (!p) return 0;
  const num = p.replace(/[¥,$]/g, "").replace(/,/g, "");
  return parseInt(num, 10) || 0;
}
